<template>
  <div class="p-paginator">
    <button
        class="p-paginator-element"
        @click="changePage(0)"
        :disabled="page === 0"
        :class="{'p-disabled': page === 0}"
    >
      «
    </button>
    <button
        class="p-paginator-element"
        @click="changePage(page - 1)"
        :disabled="page === 0"
        :class="{'p-disabled': page === 0}"
    >
      ‹
    </button>

    <button
        v-if="props.totalRecords > 0"
        class="p-paginator-element"
        @click="changePage(0)"
        :class="{ 'p-highlight': page === 0 }"
    >
      1
    </button>

    <button
        class="p-paginator-element ellipsis"
        v-if="page > 2"
    >
      …
    </button>

    <!-- Middle pages -->
    <button
        class="p-paginator-element"
        v-for="p in middlePages(page, pageCount)"
        :key="p"
        @click="changePage(p)"
        :class="{ 'p-highlight': p === page }"
    >
      {{ p + 1 }}
    </button>

    <button
        class="p-paginator-element ellipsis"
        v-if="page < pageCount - 3"
    >
      …
    </button>

    <!-- Last page -->
    <button
        class="p-paginator-element"
        v-if="pageCount > 1"
        @click="changePage(pageCount - 1)"
        :class="{ 'p-highlight': page === pageCount - 1 }"
    >
      {{ pageCount }}
    </button>

    <button
        class="p-paginator-element"
        @click="changePage(page + 1)"
        :disabled="page >= pageCount - 1"
        :class="{'p-disabled': page >= pageCount - 1}"
    >
      ›
    </button>
    <button
        class="p-paginator-element"
        @click="changePage(pageCount - 1)"
        :disabled="page >= pageCount - 1"
        :class="{'p-disabled': page >= pageCount - 1}"
    >
      »
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  first: {
    type: Number,
    default: 0,
  },
  rows: {
    type: Number,
    default: 10,
  },
  totalRecords: {
    type: Number,
    default: 0,
  },
  rowsPerPageOptions: {
    type: Array,
    default: null,
  },
})

const emit = defineEmits(['firstChange'])
const page = computed(() => (props.rows > 0 ? Math.floor(props.first / props.rows) : 0))
const pageCount = computed(() => (props.rows > 0 ? Math.ceil(props.totalRecords / props.rows) : 1))

function changePage(newPage) {
  const clamped = Math.max(0, Math.min(newPage, pageCount.value - 1))
  if (clamped === page.value) {
    return
  }

  const newFirst = clamped * props.rows
  emit('firstChange', newFirst)
}

function middlePages(currentPage, total) {
  const pages = []
  const start = Math.max(1, currentPage - 1)
  const end = Math.min(total - 2, currentPage <= 3 ? 4 : currentPage + 1)
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
}
</script>

<style scoped>
.p-paginator span {
  margin: 0;
  cursor: default;
}

/* paginator */
.p-paginator .p-paginator-element {
  border: 1px solid var(--table-paging-border);
  border-radius: 0;
  padding: 0.375rem 0.75rem;
  min-width: 1.5em;
  height: 36px;
  margin: 0 0;
  font-size: 16px;
  font-weight: normal;
}

.p-paginator .p-paginator-element.p-disabled {
  background-color: var(--table-paging-disabled);
  color: black;
}

.p-paginator > .p-paginator-element:first-of-type {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.p-paginator > .p-paginator-element:last-of-type {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.p-paginator .p-paginator-element.p-highlight {
  background-color: var(--table-blue);
  color: white;
  border-color: var(--table-blue);
}

.p-paginator .p-paginator-element:hover:not(.p-highlight):not(.p-disabled) {
  color: inherit;
  background: var(--table-paging-border);
}

.p-paginator .p-paginator-element.ellipsis {
  padding: 0.375rem 0.75rem;
  cursor: default;
}
</style>